/**
 * Iter 84.3 / Phase E5 Sprint 3 — TDS Returns Console.
 *
 * Combines Form 24Q (D36 — Quarterly TDS Return, NSDL .txt format)
 * and Form 16 Part B (D37 — Annual TDS Certificate, CSV at v5.2).
 *
 * Both share D36-F / D37-E snapshot architecture — generation creates
 * IMMUTABLE batches; re-issues are new batches.
 */
import { useCallback, useEffect, useState } from "react";
import {
    Sparkle, DownloadSimple, ArrowsClockwise, Lock, Eye,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
    DialogDescription,
} from "../../components/ui/dialog";

const fmtMoney = (v) =>
    v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const TABS = [
    { key: "24q", label: "Form 24Q (Quarterly)" },
    { key: "16b", label: "Form 16 Part B (Annual)" },
];

export default function TdsReturnsConsole() {
    const { can } = useAuth();
    const canAdmin = can("compliance:admin");
    const [tab, setTab] = useState("24q");

    return (
        <div className="p-6 space-y-4" data-testid="tds-returns-console">
            <PageHeader
                title="TDS Returns"
                subtitle="Form 24Q quarterly + Form 16 Part B annual · D36-E challan locking · D36-F + D37-E snapshot architecture"
            />
            <div className="flex gap-1 border-b">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        className={`px-4 py-2 text-sm border-b-2 ${tab === t.key ? "border-blue-600 text-blue-700 font-semibold" : "border-transparent text-slate-500 hover:text-slate-700"}`}
                        data-testid={`tds-tab-${t.key}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>
            {tab === "24q" && <Form24QTab canAdmin={canAdmin} />}
            {tab === "16b" && <Form16BTab canAdmin={canAdmin} />}
        </div>
    );
}


// ═════════════════════════════════════════════════════════════════════
//  Form 24Q Tab
// ═════════════════════════════════════════════════════════════════════
function Form24QTab({ canAdmin }) {
    const [batches, setBatches] = useState([]);
    const [runs, setRuns] = useState([]);
    const [challans, setChallans] = useState([]);
    const [fy, setFy] = useState("2025-2026");
    const [quarter, setQuarter] = useState("Q1");
    const [selectedRuns, setSelectedRuns] = useState([]);
    const [selectedChallans, setSelectedChallans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [preview, setPreview] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    const reload = useCallback(async () => {
        try {
            const [b, r, c] = await Promise.all([
                api.get(`/compliance/24q/batches?financial_year=${fy}`),
                api.get("/compliance/eligible-payroll-runs?limit=100"),
                api.get(`/compliance/tds-challans?financial_year=${fy}&quarter=${quarter}`),
            ]);
            setBatches(b.data.items || []);
            setRuns(r.data.items || []);
            setChallans(c.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Load failed"));
        }
    }, [fy, quarter]);
    useEffect(() => { reload(); }, [reload]);

    const toggleRun = (id) =>
        setSelectedRuns((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
    const toggleChallan = (id) =>
        setSelectedChallans((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

    const handlePreview = async () => {
        if (selectedRuns.length === 0) { toast.error("Select at least 1 payroll run"); return; }
        if (selectedChallans.length === 0) { toast.error("Select at least 1 TDS challan for the quarter"); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/compliance/24q/preview", {
                financial_year: fy, quarter,
                payroll_run_ids: selectedRuns,
                challan_ids: selectedChallans,
            });
            setPreview(data);
            setPreviewOpen(true);
        } catch (err) {
            toast.error(formatApiError(err, "Preview failed"));
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async () => {
        if (selectedRuns.length === 0) { toast.error("Select at least 1 payroll run"); return; }
        if (selectedChallans.length === 0) { toast.error("Select at least 1 TDS challan for the quarter"); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/compliance/24q/generate", {
                financial_year: fy, quarter,
                payroll_run_ids: selectedRuns,
                challan_ids: selectedChallans,
                note: `${quarter} ${fy} via TDS Returns console`,
            });
            toast.success(`Form 24Q generated · ${data.n_deductees} deductees · TDS ${fmtMoney(data.total_tds)}`);
            await handleDownload(data.id, data.file_name);
            setPreviewOpen(false);
            setSelectedRuns([]);
            setSelectedChallans([]);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Generate failed"));
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (batchId, filename) => {
        try {
            const r = await api.get(`/compliance/24q/batches/${batchId}/download`, { responseType: "blob" });
            const blob = new Blob([r.data], { type: "text/plain" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = filename || `24Q_${batchId}.txt`; a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err, "Download failed"));
        }
    };

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded p-4 bg-slate-50/30 space-y-3" data-testid="form24q-builder">
                    <h3 className="font-semibold text-sm">Build Form 24Q</h3>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-xs">FY</Label>
                            <Input value={fy} onChange={(e) => setFy(e.target.value)} className="font-mono" data-testid="form24q-fy-input" />
                        </div>
                        <div>
                            <Label className="text-xs">Quarter</Label>
                            <select value={quarter} onChange={(e) => setQuarter(e.target.value)} className="border rounded px-2 py-1 text-sm font-mono w-full" data-testid="form24q-quarter-select">
                                <option value="Q1">Q1 (Apr-Jun)</option>
                                <option value="Q2">Q2 (Jul-Sep)</option>
                                <option value="Q3">Q3 (Oct-Dec)</option>
                                <option value="Q4">Q4 (Jan-Mar) · Annexure II</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Payroll Runs ({selectedRuns.length} selected)</Label>
                        <div className="border rounded max-h-40 overflow-y-auto bg-white text-xs" data-testid="form24q-runs-list">
                            {runs.length === 0 ? (
                                <div className="text-slate-400 p-2 text-center">No eligible runs</div>
                            ) : runs.map((r) => (
                                <label key={r.id} className="flex items-start gap-2 p-1.5 border-b hover:bg-slate-50 cursor-pointer">
                                    <input type="checkbox" checked={selectedRuns.includes(r.id)} onChange={() => toggleRun(r.id)} data-testid={`form24q-run-${r.id}`} />
                                    <div className="flex-1">
                                        <div className="font-mono">{r.id}</div>
                                        <div className="text-slate-500 text-[10px]">{r.label || `${r.period_start} → ${r.period_end}`} · {r.status}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">TDS Challans for {quarter} ({selectedChallans.length} selected)</Label>
                        <div className="border rounded max-h-32 overflow-y-auto bg-white text-xs" data-testid="form24q-challans-list">
                            {challans.length === 0 ? (
                                <div className="text-slate-400 p-2 text-center">No challans for {fy} {quarter} — capture them first under TDS Challans</div>
                            ) : challans.map((c) => {
                                const locked = (c.locked_in_filing_ids || []).length > 0;
                                return (
                                    <label key={c.id} className={`flex items-start gap-2 p-1.5 border-b text-xs hover:bg-slate-50 ${locked ? "opacity-60" : "cursor-pointer"}`}>
                                        <input type="checkbox" checked={selectedChallans.includes(c.id)} onChange={() => toggleChallan(c.id)} disabled={locked} data-testid={`form24q-challan-${c.id}`} />
                                        <div className="flex-1 font-mono text-[10px]">
                                            {c.cin} · {fmtMoney(c.amount_deposited)} · {c.period_label}
                                            {locked && <span className="ml-2 text-blue-700">🔒 already filed</span>}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button size="sm" variant="outline" disabled={loading} onClick={handlePreview} data-testid="form24q-preview-btn">
                            <Eye size={14} className="mr-1" /> Preview
                        </Button>
                        {canAdmin && (
                            <Button size="sm" disabled={loading} onClick={handleGenerate} data-testid="form24q-generate-btn">
                                <Sparkle size={14} className="mr-1" /> Generate
                            </Button>
                        )}
                    </div>
                </div>

                <div className="border rounded p-4 bg-slate-50/30 text-xs space-y-2">
                    <h3 className="font-semibold text-sm">Form 24Q — D36 Locks</h3>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>D36-E:</strong> A challan referenced by a Generated 24Q is locked from edit/delete. Corrections require a new challan + revised 24Q.</li>
                        <li><strong>D36-F:</strong> Each generation creates immutable <code>form24q_batches</code> + <code>form24q_records</code>. Re-runs are new batches.</li>
                        <li><strong>Annexure II</strong> emitted only for Q4 (full-FY salary summary per employee).</li>
                        <li><strong>PAN handling:</strong> Missing PAN = warning; format-invalid PAN = ERROR (engine blocks generation).</li>
                        <li>Output: pipe-delimited NSDL .txt — validate locally via NSDL FVU before portal upload.</li>
                    </ul>
                </div>
            </div>

            <div className="border rounded" data-testid="form24q-batches-table">
                <div className="p-2 bg-slate-50 border-b text-sm font-semibold">Generated 24Q Batches · {batches.length}</div>
                <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b uppercase text-slate-600 text-[10px]">
                        <tr>
                            <th className="text-left px-2 py-1">Batch</th>
                            <th className="text-left px-2 py-1">FY · Q</th>
                            <th className="text-left px-2 py-1">Period</th>
                            <th className="text-right px-2 py-1">Challans</th>
                            <th className="text-right px-2 py-1">Deductees</th>
                            <th className="text-right px-2 py-1">Total TDS</th>
                            <th className="text-left px-2 py-1">Generated</th>
                            <th className="text-left px-2 py-1">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {batches.length === 0 && (
                            <tr><td colSpan="8" className="text-center text-slate-400 p-6">No 24Q batches yet</td></tr>
                        )}
                        {batches.map((b) => (
                            <tr key={b.id} className="border-b" data-testid={`form24q-row-${b.id}`}>
                                <td className="px-2 py-1 font-mono">{b.id}</td>
                                <td className="px-2 py-1 font-mono">{b.financial_year} · {b.quarter}</td>
                                <td className="px-2 py-1 text-[10px]">{b.period_from} → {b.period_to}</td>
                                <td className="px-2 py-1 text-right font-mono">{b.n_challans}</td>
                                <td className="px-2 py-1 text-right font-mono">{b.n_deductees}{b.has_annexure_ii ? ` + ${b.n_employees_q4} EM` : ""}</td>
                                <td className="px-2 py-1 text-right font-mono">{fmtMoney(b.total_tds)}</td>
                                <td className="px-2 py-1 text-[10px] text-slate-500">{b.generated_by_email}<br />{b.generated_at?.slice(0, 16).replace("T", " ")}</td>
                                <td className="px-2 py-1">
                                    <Button size="sm" variant="outline" onClick={() => handleDownload(b.id, b.file_name)} data-testid={`form24q-download-${b.id}`}>
                                        <DownloadSimple size={12} />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Form 24Q Preview · {preview?.quarter} {preview?.financial_year}</DialogTitle>
                        <DialogDescription className="text-xs">TAN {preview?.tan} · Period {preview?.period_from} → {preview?.period_to}</DialogDescription>
                    </DialogHeader>
                    {preview && (
                        <div className="text-xs space-y-3">
                            <div className="grid grid-cols-4 gap-2">
                                <div className="border rounded p-2 bg-slate-50">
                                    <div className="text-[9px] uppercase text-slate-500">Challans</div>
                                    <div className="font-mono font-bold">{preview.n_challans}</div>
                                </div>
                                <div className="border rounded p-2 bg-slate-50">
                                    <div className="text-[9px] uppercase text-slate-500">Deductees</div>
                                    <div className="font-mono font-bold">{preview.n_deductees}</div>
                                </div>
                                <div className="border rounded p-2 bg-slate-50">
                                    <div className="text-[9px] uppercase text-slate-500">Total TDS</div>
                                    <div className="font-mono font-bold">{fmtMoney(preview.totals.total_tds)}</div>
                                </div>
                                <div className="border rounded p-2 bg-slate-50">
                                    <div className="text-[9px] uppercase text-slate-500">Deposited</div>
                                    <div className="font-mono font-bold">{fmtMoney(preview.totals.total_deposited)}</div>
                                </div>
                            </div>
                            {preview.n_pan_invalid > 0 && (
                                <div className="bg-rose-50 border border-rose-300 rounded p-2 text-rose-900">
                                    <strong>{preview.n_pan_invalid}</strong> deductees have INVALID PAN format. Generation will be BLOCKED (D38-D).
                                </div>
                            )}
                            {preview.n_missing_pan > 0 && (
                                <div className="bg-amber-50 border border-amber-300 rounded p-2 text-amber-900">
                                    <strong>{preview.n_missing_pan}</strong> deductees have NO PAN — generation will proceed (warning only, D38-D).
                                </div>
                            )}
                            <div className="max-h-72 overflow-y-auto border rounded">
                                <table className="w-full text-[10px]">
                                    <thead className="bg-slate-100 sticky top-0">
                                        <tr>
                                            <th className="text-left px-1 py-0.5">Code</th>
                                            <th className="text-left px-1 py-0.5">Name</th>
                                            <th className="text-left px-1 py-0.5">PAN</th>
                                            <th className="text-left px-1 py-0.5">Period</th>
                                            <th className="text-right px-1 py-0.5">Taxable</th>
                                            <th className="text-right px-1 py-0.5">TDS</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(preview.deductees || []).map((d, i) => (
                                            <tr key={i} className="border-b">
                                                <td className="px-1 py-0.5 font-mono">{d.employee_code}</td>
                                                <td className="px-1 py-0.5">{d.employee_name}</td>
                                                <td className="px-1 py-0.5 font-mono">{d.pan || <span className="text-rose-600">—</span>}</td>
                                                <td className="px-1 py-0.5">{d.period_label}</td>
                                                <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(d.taxable_amount)}</td>
                                                <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(d.tds_amount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
                        {canAdmin && <Button onClick={handleGenerate} data-testid="form24q-generate-from-preview-btn">Generate & Download</Button>}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


// ═════════════════════════════════════════════════════════════════════
//  Form 16 Part B Tab
// ═════════════════════════════════════════════════════════════════════
function Form16BTab({ canAdmin }) {
    const [batches, setBatches] = useState([]);
    const [fy, setFy] = useState("2025-2026");
    const [filterMode, setFilterMode] = useState("ALL");
    const [department, setDepartment] = useState("");
    const [businessUnit, setBusinessUnit] = useState("");
    const [onlyWithTds, setOnlyWithTds] = useState(true);
    const [loading, setLoading] = useState(false);
    const [preview, setPreview] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    const reload = useCallback(async () => {
        try {
            const { data } = await api.get(`/compliance/16b/batches?financial_year=${fy}`);
            setBatches(data.items || []);
        } catch (err) { /* non-fatal */ }
    }, [fy]);
    useEffect(() => { reload(); }, [reload]);

    const buildBody = () => ({
        financial_year: fy,
        filter_mode: filterMode,
        department: department || null,
        business_unit: businessUnit || null,
        only_with_tds: onlyWithTds,
    });

    const handlePreview = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/compliance/16b/preview", buildBody());
            setPreview(data);
            setPreviewOpen(true);
        } catch (err) {
            toast.error(formatApiError(err, "Preview failed"));
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/compliance/16b/bulk-generate", {
                ...buildBody(),
                note: `${filterMode} via Form 16B console`,
            });
            toast.success(`Form 16 batch generated · ${data.n_employees} employees · ${fmtMoney(data.total_tds_deducted)} TDS`);
            setPreviewOpen(false);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Generate failed"));
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadCsv = async (batchId) => {
        try {
            const r = await api.get(`/compliance/16b/batches/${batchId}/csv`, { responseType: "blob" });
            const blob = new Blob([r.data], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `Form16B_${batchId}.csv`; a.click();
            URL.revokeObjectURL(url);
            toast.success("Form 16 CSV downloaded");
        } catch (err) {
            toast.error(formatApiError(err, "Download failed"));
        }
    };

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded p-4 bg-slate-50/30 space-y-3" data-testid="form16b-builder">
                    <h3 className="font-semibold text-sm">Generate Form 16 Part B</h3>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-xs">Financial Year</Label>
                            <Input value={fy} onChange={(e) => setFy(e.target.value)} className="font-mono" data-testid="form16b-fy-input" />
                        </div>
                        <div>
                            <Label className="text-xs">Filter Mode (D37-F)</Label>
                            <select value={filterMode} onChange={(e) => setFilterMode(e.target.value)} className="border rounded px-2 py-1 text-sm w-full font-mono" data-testid="form16b-filter-mode">
                                <option value="ALL">All Employees</option>
                                <option value="DEPARTMENT">By Department</option>
                                <option value="BUSINESS_UNIT">By Business Unit</option>
                                <option value="SELECTED">Selected Employees</option>
                            </select>
                        </div>
                    </div>
                    {filterMode === "DEPARTMENT" && (
                        <div>
                            <Label className="text-xs">Department</Label>
                            <Input value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="e.g. Engineering" data-testid="form16b-department-input" />
                        </div>
                    )}
                    {filterMode === "BUSINESS_UNIT" && (
                        <div>
                            <Label className="text-xs">Business Unit</Label>
                            <Input value={businessUnit} onChange={(e) => setBusinessUnit(e.target.value)} placeholder="e.g. BU1" data-testid="form16b-bu-input" />
                        </div>
                    )}
                    <label className="flex items-center gap-2 text-xs">
                        <input type="checkbox" checked={onlyWithTds} onChange={(e) => setOnlyWithTds(e.target.checked)} data-testid="form16b-only-with-tds" />
                        Only issue to employees with TDS &gt; 0 (recommended)
                    </label>
                    <div className="flex gap-2">
                        <Button size="sm" variant="outline" disabled={loading} onClick={handlePreview} data-testid="form16b-preview-btn">
                            <Eye size={14} className="mr-1" /> Preview
                        </Button>
                        {canAdmin && (
                            <Button size="sm" disabled={loading} onClick={handleGenerate} data-testid="form16b-generate-btn">
                                <Sparkle size={14} className="mr-1" /> Bulk Generate
                            </Button>
                        )}
                    </div>
                </div>

                <div className="border rounded p-4 bg-slate-50/30 text-xs space-y-2">
                    <h3 className="font-semibold text-sm">Form 16 Part B — D37 Locks</h3>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>D37-E:</strong> Each generation creates immutable <code>form16_batches</code> + <code>form16_employees</code> rows. Re-issues are new batches.</li>
                        <li><strong>D37-F:</strong> Bulk filters — All / Department / Business Unit / Selected — all populations supported.</li>
                        <li><strong>D37-C:</strong> Re-issue allowed; original is preserved (audit-grade).</li>
                        <li><strong>PAN handling:</strong> Format-invalid PAN BLOCKS generation; missing PAN allowed as warning.</li>
                        <li>Output: per-batch CSV at v5.2 (PDF rendering via Playwright is on the v5.3 backlog).</li>
                    </ul>
                </div>
            </div>

            <div className="border rounded" data-testid="form16b-batches-table">
                <div className="p-2 bg-slate-50 border-b text-sm font-semibold">Form 16 Batches · {batches.length}</div>
                <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b uppercase text-slate-600 text-[10px]">
                        <tr>
                            <th className="text-left px-2 py-1">Batch</th>
                            <th className="text-left px-2 py-1">FY</th>
                            <th className="text-left px-2 py-1">Filter</th>
                            <th className="text-right px-2 py-1">Employees</th>
                            <th className="text-right px-2 py-1">Total TDS</th>
                            <th className="text-left px-2 py-1">Generated</th>
                            <th className="text-left px-2 py-1">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {batches.length === 0 && (
                            <tr><td colSpan="7" className="text-center text-slate-400 p-6">No Form 16 batches yet</td></tr>
                        )}
                        {batches.map((b) => (
                            <tr key={b.id} className="border-b" data-testid={`form16b-row-${b.id}`}>
                                <td className="px-2 py-1 font-mono">{b.id}</td>
                                <td className="px-2 py-1">{b.financial_year}</td>
                                <td className="px-2 py-1 text-[10px]">
                                    <Badge className="bg-slate-100 text-slate-700">{b.filter_mode}</Badge>
                                    {b.department && <span className="ml-1">{b.department}</span>}
                                    {b.business_unit && <span className="ml-1">{b.business_unit}</span>}
                                </td>
                                <td className="px-2 py-1 text-right font-mono">{b.n_employees}</td>
                                <td className="px-2 py-1 text-right font-mono">{fmtMoney(b.total_tds_deducted)}</td>
                                <td className="px-2 py-1 text-[10px] text-slate-500">{b.generated_by_email}<br />{b.generated_at?.slice(0, 16).replace("T", " ")}</td>
                                <td className="px-2 py-1">
                                    {canAdmin && (
                                        <Button size="sm" variant="outline" onClick={() => handleDownloadCsv(b.id)} data-testid={`form16b-download-${b.id}`}>
                                            <DownloadSimple size={12} className="mr-1" /> CSV
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Form 16B Preview · FY {preview?.financial_year}</DialogTitle>
                        <DialogDescription className="text-xs">{preview?.tan} · {preview?.employer_name}</DialogDescription>
                    </DialogHeader>
                    {preview && (
                        <div className="text-xs space-y-3">
                            <div className="grid grid-cols-3 gap-2">
                                <div className="border rounded p-2 bg-slate-50">
                                    <div className="text-[9px] uppercase text-slate-500">Total Employees</div>
                                    <div className="font-mono font-bold">{preview.n_employees}</div>
                                </div>
                                <div className="border rounded p-2 bg-slate-50">
                                    <div className="text-[9px] uppercase text-slate-500">Eligible (TDS &gt; 0)</div>
                                    <div className="font-mono font-bold">{preview.n_eligible}</div>
                                </div>
                                <div className="border rounded p-2 bg-slate-50">
                                    <div className="text-[9px] uppercase text-slate-500">Total TDS</div>
                                    <div className="font-mono font-bold">{fmtMoney(preview.totals.total_tds_deducted)}</div>
                                </div>
                            </div>
                            <div className="max-h-72 overflow-y-auto border rounded">
                                <table className="w-full text-[10px]">
                                    <thead className="bg-slate-100 sticky top-0">
                                        <tr>
                                            <th className="text-left px-1 py-0.5">Code</th>
                                            <th className="text-left px-1 py-0.5">Name</th>
                                            <th className="text-left px-1 py-0.5">PAN</th>
                                            <th className="text-right px-1 py-0.5">Gross FY</th>
                                            <th className="text-right px-1 py-0.5">Tax</th>
                                            <th className="text-right px-1 py-0.5">TDS</th>
                                            <th className="text-right px-1 py-0.5">Refund</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(preview.employees || []).slice(0, 200).map((e) => (
                                            <tr key={e.employee_id} className="border-b">
                                                <td className="px-1 py-0.5 font-mono">{e.employee_code}</td>
                                                <td className="px-1 py-0.5">{e.employee_name}</td>
                                                <td className="px-1 py-0.5 font-mono">{e.pan || <span className="text-rose-600">—</span>}</td>
                                                <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(e.gross_salary_fy)}</td>
                                                <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(e.total_tax_liability)}</td>
                                                <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(e.tds_deducted_fy)}</td>
                                                <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(e.refund_or_payable)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
                        {canAdmin && <Button onClick={handleGenerate} data-testid="form16b-generate-from-preview-btn">Bulk Generate</Button>}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
